import { useEffect, useRef } from 'react';

import mixpanel from 'mixpanel-browser';

import { useLastFactFindField } from './useLastFactFindField';

const EXITED_FACT_FIND_EVENT_NAME = 'exited_fact_find';
const RETURNED_FACT_FIND_EVENT_NAME = 'returned_fact_find';

const TRACKED_FACT_FIND_PATHS = [
    '/eligibility/applicants',
    '/eligibility/applicants2',
    '/eligibility/property-details',
    '/eligibility/income',
    '/eligibility/income2',
    '/eligibility/outgoings',
];

const isTrackedFactFindPath = (pathname: string) =>
    TRACKED_FACT_FIND_PATHS.includes(pathname.replace(/\/$/, ''));

export const useTrackLastFactFindField = (
    pathname: string,
    loggedIn: boolean,
) => {
    const isTrackingEnabled = isTrackedFactFindPath(pathname);
    const lastFieldRef = useLastFactFindField(pathname, isTrackingEnabled);
    const wasLoggedInRef = useRef(loggedIn);
    const hasSentHiddenRef = useRef(false);

    useEffect(() => {
        if (loggedIn && !wasLoggedInRef.current) {
            lastFieldRef.current = {
                page: lastFieldRef.current.page,
                last_interacted_field: '',
            };
        }
        wasLoggedInRef.current = loggedIn;
    }, [loggedIn, lastFieldRef]);

    useEffect(() => {
        const handleHidden = () => {
            if (lastFieldRef.current.last_interacted_field) {
                mixpanel.track(
                    EXITED_FACT_FIND_EVENT_NAME,
                    lastFieldRef.current,
                    { transport: 'sendBeacon' },
                );
                hasSentHiddenRef.current = true;
            }
        };

        const handleReturned = () => {
            if (!hasSentHiddenRef.current) {
                return;
            }

            hasSentHiddenRef.current = false;

            mixpanel.track(RETURNED_FACT_FIND_EVENT_NAME, {
                page: lastFieldRef.current.page,
            });
        };

        const handleVisibilityChange = () => {
            if (!isTrackingEnabled) {
                return;
            }

            if (document.visibilityState === 'hidden') {
                handleHidden();
            } else if (document.visibilityState === 'visible') {
                handleReturned();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            document.removeEventListener(
                'visibilitychange',
                handleVisibilityChange,
            );
        };
    }, [isTrackingEnabled, lastFieldRef]);
};
